import React, { useState, useRef, useLayoutEffect, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Search } from 'lucide-react';
import { useAnimals } from '../../../hooks/useAnimals';
import IdentidadeDoAnimal from './IdentidadeDoAnimal';
import QuickAnimalRegister from '../modals/QuickAnimalRegister';

/**
 * Buscar um animal e, não achando, cadastrá-lo sem sair da linha.
 *
 * O mesmo bloco — campo, lista de resultados, "+ Cadastrar" e o modal de
 * cadastro rápido — estava copiado em cada célula que escolhe um animal, com
 * o estado morando no componente-pai indexado por linha (`searchTerm[index]`,
 * `searchResults[index]`, `currentDonorIndex`...). Seis variáveis de estado e
 * seis handlers por tipo de animal, no meio da aba que cuida de oócitos.
 *
 * Aqui o estado é do próprio campo, que é de quem ele sempre foi: quem digita
 * numa linha não muda a busca da linha de baixo. O pai só diz o que fazer com
 * o animal escolhido.
 *
 * `filtro` existe porque a busca do banco não sabe o que se procura: ela casa
 * por brinco ou RGD e devolve doadora, touro e receptora juntos. Quem está
 * escolhendo o touro não quer ver a vaca de mesmo nome.
 *
 * A LISTA MORA NO <body>, NÃO NA LINHA
 *   Dentro da célula ela ficava presa à linha: a linha de embrião transferido
 *   tem `opacity`, o que cria um contexto de empilhamento — a lista herdava a
 *   transparência e as linhas de baixo pintavam por cima dela e roubavam o
 *   clique. E o `overflow` da tabela e do modal a cortavam. Em portal, com
 *   `position: fixed` medido do campo, ela fica acima de tudo do modal (z-50)
 *   e abaixo do cadastro rápido (z-[60]), e abre para cima quando falta espaço.
 */
const ALTURA_MAXIMA = 240;
const LARGURA_MINIMA = 208;

const BuscaDeAnimal = ({
  valor,
  onDigitar,
  onEscolher,
  laboratoryId,
  filtro = null,
  categoriaPadrao = null,
  clients = [],
  clientIdPadrao = null,
  placeholder = 'Buscar...',
  className = '',
  // A tabela da FIV é densa: vinte doadoras na tela, célula de meia altura. O
  // campo padrão é o do formulário; quem está numa tabela passa o seu.
  inputClassName = 'w-full px-2 py-1 pr-6 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500',
}) => {
  const { searchAnimals, quickRegister, loading } = useAnimals(laboratoryId);
  const [termo, setTermo] = useState(undefined);
  const [resultados, setResultados] = useState([]);
  const [cadastrando, setCadastrando] = useState(false);
  const [fechada, setFechada] = useState(false);
  const [destaque, setDestaque] = useState(-1);
  const [posicao, setPosicao] = useState(null);
  const campoRef = useRef(null);
  const listaRef = useRef(null);
  const ultimaBusca = useRef(0);
  const listaId = useId();

  const digitado = termo !== undefined ? termo : (valor || '');

  // Cada tecla dispara uma busca, e elas não voltam na ordem em que saíram:
  // a de "13" chegando depois da de "139" trocava a lista por 137 e 138. Só a
  // resposta da última busca vale.
  const buscar = async (t) => {
    const minha = ++ultimaBusca.current;
    setTermo(t);
    setFechada(false);
    setDestaque(-1);
    onDigitar?.(t);
    if (t.length < 2) { setResultados([]); return; }
    const achados = await searchAnimals(t);
    if (minha !== ultimaBusca.current) return;
    setResultados(filtro ? (achados || []).filter(filtro) : (achados || []));
  };

  const escolher = (animal) => {
    ultimaBusca.current++;
    onEscolher?.(animal);
    setTermo(undefined);
    setResultados([]);
    setDestaque(-1);
  };

  const abrirCadastro = () => { setFechada(true); setCadastrando(true); };

  const salvarCadastro = async (dados) => {
    const novo = await quickRegister(dados);
    if (novo) { escolher(novo); setCadastrando(false); }
  };

  // A lista abre a partir de duas letras: com uma, a busca casa com meio
  // rebanho e a lista atrapalha mais do que ajuda.
  const aberta = digitado.length >= 2 && termo !== undefined && !fechada;

  // Mede o campo e decide para que lado abrir. Refaz a cada rolagem (de
  // qualquer contêiner: o modal e a tabela rolam por conta própria) e resize.
  useLayoutEffect(() => {
    if (!aberta) { setPosicao(null); return undefined; }
    const medir = () => {
      const r = campoRef.current?.getBoundingClientRect();
      if (!r) return;
      const abaixo = window.innerHeight - r.bottom - 8;
      const acima = r.top - 8;
      const paraCima = abaixo < ALTURA_MAXIMA && acima > abaixo;
      const largura = Math.max(r.width, LARGURA_MINIMA);
      setPosicao({
        left: Math.max(8, Math.min(r.left, window.innerWidth - largura - 8)),
        width: largura,
        maxHeight: Math.min(ALTURA_MAXIMA, paraCima ? acima : abaixo),
        ...(paraCima ? { bottom: window.innerHeight - r.top + 2 } : { top: r.bottom + 2 }),
      });
    };
    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [aberta]);

  // Clique fora fecha. A lista não é filha do campo no DOM, então os dois
  // contam como "dentro".
  useEffect(() => {
    if (!aberta) return undefined;
    const fora = (e) => {
      if (campoRef.current?.contains(e.target) || listaRef.current?.contains(e.target)) return;
      setFechada(true);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('touchstart', fora);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('touchstart', fora);
    };
  }, [aberta]);

  // Mantém a opção destacada à vista quando as setas passam da borda.
  useEffect(() => {
    if (destaque < 0) return;
    listaRef.current?.querySelector(`[data-opcao="${destaque}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [destaque]);

  // Setas andam pelas opções; a última é sempre "+ Cadastrar".
  const teclar = (e) => {
    if (!aberta) {
      if (e.key === 'ArrowDown' && fechada) setFechada(false);
      return;
    }
    const total = resultados.length + 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestaque(d => (d + 1) % total);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestaque(d => (d <= 0 ? total - 1 : d - 1));
    } else if (e.key === 'Enter' && destaque >= 0) {
      e.preventDefault();
      if (destaque < resultados.length) escolher(resultados[destaque]);
      else abrirCadastro();
    } else if (e.key === 'Escape') {
      // Fecha só a lista, não o modal do workflow por trás.
      e.stopPropagation();
      setFechada(true);
    }
  };

  const opcao = (i) => `w-full text-left px-2 py-1.5 text-xs ${
    i === destaque ? 'bg-blue-50' : 'hover:bg-blue-50'}`;

  return (
    <div className={`relative ${className}`}>
      <div className="relative" ref={campoRef}>
        <input
          type="text"
          placeholder={placeholder}
          className={inputClassName}
          value={digitado}
          onChange={(e) => buscar(e.target.value)}
          onKeyDown={teclar}
          onFocus={() => setFechada(false)}
          role="combobox"
          aria-expanded={aberta}
          aria-controls={listaId}
          aria-autocomplete="list"
        />
        <Search className="absolute right-1.5 top-1.5 w-3 h-3 text-slate-400 pointer-events-none" />
      </div>

      {aberta && posicao && createPortal(
        <div
          ref={listaRef}
          id={listaId}
          role="listbox"
          style={posicao}
          className="fixed z-[55] bg-white border border-slate-200 rounded shadow-lg overflow-y-auto"
        >
          {resultados.map((animal, i) => (
            <button
              key={animal.id}
              type="button"
              role="option"
              aria-selected={i === destaque}
              data-opcao={i}
              onClick={() => escolher(animal)}
              onMouseEnter={() => setDestaque(i)}
              className={`${opcao(i)} border-b border-slate-100 last:border-0`}
            >
              <IdentidadeDoAnimal animal={animal} />
            </button>
          ))}
          <button
            type="button"
            role="option"
            aria-selected={destaque === resultados.length}
            data-opcao={resultados.length}
            onClick={abrirCadastro}
            onMouseEnter={() => setDestaque(resultados.length)}
            className={`${opcao(resultados.length)} text-blue-600 border-t border-slate-100 font-medium`}
          >
            + Cadastrar "{digitado}"
          </button>
        </div>,
        document.body
      )}

      <QuickAnimalRegister
        show={cadastrando}
        onClose={() => setCadastrando(false)}
        onSave={salvarCadastro}
        loading={loading}
        initialCategory={categoriaPadrao}
        initialIdentifier={digitado}
        clients={clients}
        initialClientId={clientIdPadrao}
      />
    </div>
  );
};

export default BuscaDeAnimal;
